import { apiFetch } from '../providers/api-rest-provider';
import { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  Create,
  Edit,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextInput,
  useRecordContext,
  useRedirect,
} from 'react-admin';
import {
  Chip,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormGroup,
  FormControlLabel,
  TextField as MuiTextField,
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ReferenceLine,
  Legend,
} from 'recharts';
import { useController } from 'react-hook-form';
import { ScopePicker } from '../components/ScopePicker';
import { ArchivalToolbar } from '../components/ArchivalToolbar';

// ---------------------------------------------------------------------------
// Domain tag config
// ---------------------------------------------------------------------------

type DomainTag = 'physical' | 'psychological' | 'cognitive' | 'social' | 'spiritual' | 'family';

const DOMAIN_COLORS: Record<DomainTag, 'error' | 'secondary' | 'primary' | 'success' | 'warning' | 'default'> = {
  physical: 'error',
  psychological: 'secondary',
  cognitive: 'primary',
  social: 'success',
  spiritual: 'warning',
  family: 'default',
};

const ALL_DOMAINS: DomainTag[] = [
  'physical',
  'psychological',
  'cognitive',
  'social',
  'spiritual',
  'family',
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type GoalDirection = 'higher_is_better' | 'lower_is_better';
type ReviewCadence = 'weekly' | 'monthly' | 'quarterly';

interface MetricGoal {
  metric: string;
  target: number;
  direction: GoalDirection;
  reviewCadence: ReviewCadence;
}

// Effectiveness endpoint types
interface TrendPoint {
  period: string;
  count: number;
  sum: number;
  avg: number | null;
}

interface GoalResult {
  metric: string;
  target: number;
  direction: GoalDirection;
  reviewCadence: ReviewCadence;
  status: 'on_track' | 'off_track' | 'insufficient_data';
  current: { count: number; sum: number; avg: number | null };
  trend: TrendPoint[];
}

interface EffectivenessResponse {
  programId: string;
  programName: string;
  domainTags?: string[];
  orgPath?: string;
  asOf: string;
  offTrackCount: number;
  goals: GoalResult[];
}

interface ProgramRecord {
  id: string;
  name: string;
  domainTags?: DomainTag[];
  linkedAssessmentIds?: string[];
  metricGoals?: MetricGoal[];
  orgPath?: string;
  archived_at?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// DomainTagChips display component
// ---------------------------------------------------------------------------

const DomainTagChips = ({ tags }: { tags?: DomainTag[] }) => {
  if (!tags || tags.length === 0) return <Typography variant="body2" color="text.secondary">None</Typography>;
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {tags.map((tag) => (
        <Chip
          key={tag}
          label={tag.charAt(0).toUpperCase() + tag.slice(1)}
          size="small"
          color={DOMAIN_COLORS[tag] ?? 'default'}
        />
      ))}
    </Stack>
  );
};
DomainTagChips.displayName = 'DomainTagChips';

const DomainTagsField = () => {
  const record = useRecordContext<ProgramRecord>();
  return <DomainTagChips tags={record?.domainTags} />;
};
DomainTagsField.displayName = 'DomainTagsField';

// ---------------------------------------------------------------------------
// DomainTags multi-select input
// ---------------------------------------------------------------------------

const DomainTagsInput = () => {
  const { field } = useController({ name: 'domainTags', defaultValue: [] as DomainTag[] });
  const selected: DomainTag[] = field.value ?? [];

  const toggle = (tag: DomainTag) => {
    if (selected.includes(tag)) {
      field.onChange(selected.filter((t) => t !== tag));
    } else {
      field.onChange([...selected, tag]);
    }
  };

  return (
    <Box mb={2}>
      <Typography variant="subtitle2" gutterBottom>Domain Tags</Typography>
      <FormGroup row>
        {ALL_DOMAINS.map((tag) => (
          <FormControlLabel
            key={tag}
            control={
              <Checkbox
                checked={selected.includes(tag)}
                onChange={() => toggle(tag)}
                size="small"
              />
            }
            label={tag.charAt(0).toUpperCase() + tag.slice(1)}
          />
        ))}
      </FormGroup>
    </Box>
  );
};
DomainTagsInput.displayName = 'DomainTagsInput';

// ---------------------------------------------------------------------------
// ScopePicker as react-hook-form field
// ---------------------------------------------------------------------------

const ScopePickerInput = ({ source, label }: { source: string; label?: string }) => {
  const { field } = useController({ name: source, defaultValue: '' });
  return (
    <ScopePicker value={field.value ?? ''} onChange={field.onChange} label={label ?? 'Org Path'} />
  );
};
ScopePickerInput.displayName = 'ScopePickerInput';

// ---------------------------------------------------------------------------
// MetricGoals repeatable input
// ---------------------------------------------------------------------------

const MetricGoalsInput = () => {
  const { field } = useController({ name: 'metricGoals', defaultValue: [] as MetricGoal[] });
  const goals: MetricGoal[] = field.value ?? [];

  const add = () => {
    field.onChange([
      ...goals,
      { metric: '', target: 0, direction: 'higher_is_better' as GoalDirection, reviewCadence: 'monthly' as ReviewCadence },
    ]);
  };

  const remove = (idx: number) => {
    field.onChange(goals.filter((_, i) => i !== idx));
  };

  const update = (idx: number, patch: Partial<MetricGoal>) => {
    field.onChange(goals.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  };

  return (
    <Box mb={2}>
      <Typography variant="subtitle2" gutterBottom>Metric Goals</Typography>
      {goals.map((goal, idx) => (
        <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
          <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-end">
            <MuiTextField
              label="Metric name"
              size="small"
              value={goal.metric}
              onChange={(e) => update(idx, { metric: e.target.value })}
              sx={{ minWidth: 150 }}
            />
            <MuiTextField
              label="Target"
              size="small"
              type="number"
              value={goal.target}
              onChange={(e) => update(idx, { target: Number(e.target.value) })}
              sx={{ width: 100 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Direction</InputLabel>
              <Select
                label="Direction"
                value={goal.direction}
                onChange={(e) => update(idx, { direction: e.target.value as GoalDirection })}
              >
                <MenuItem value="higher_is_better">Higher is better</MenuItem>
                <MenuItem value="lower_is_better">Lower is better</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Cadence</InputLabel>
              <Select
                label="Cadence"
                value={goal.reviewCadence}
                onChange={(e) => update(idx, { reviewCadence: e.target.value as ReviewCadence })}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
              </Select>
            </FormControl>
            <Button size="small" color="error" onClick={() => remove(idx)}>Remove</Button>
          </Stack>
        </Paper>
      ))}
      <Button size="small" variant="outlined" onClick={add}>+ Add Goal</Button>
    </Box>
  );
};
MetricGoalsInput.displayName = 'MetricGoalsInput';

// ---------------------------------------------------------------------------
// ProgramList
// ---------------------------------------------------------------------------

export const ProgramList = () => (
  <List exporter={false}>
    <Datagrid rowClick="show">
      <TextField source="name" label="Name" />
      <FunctionField
        label="Domain Tags"
        render={(record: ProgramRecord) => <DomainTagChips tags={record?.domainTags} />}
      />
      <TextField source="orgPath" label="Org Path" />
      <FunctionField
        label="Metric Goals"
        render={(record: ProgramRecord) => record?.metricGoals?.length ?? 0}
      />
      <FunctionField
        label="Archived"
        render={(record: ProgramRecord) =>
          record?.archived_at ? (
            <Chip label="Archived" size="small" color="default" />
          ) : (
            <Chip label="Active" size="small" color="success" />
          )
        }
      />
    </Datagrid>
  </List>
);
ProgramList.displayName = 'ProgramList';

// ---------------------------------------------------------------------------
// ProgramShow
// ---------------------------------------------------------------------------

const ViewEffectivenessButton = () => {
  const record = useRecordContext<ProgramRecord>();
  const redirect = useRedirect();
  if (!record) return null;
  return (
    <Button
      variant="outlined"
      size="small"
      onClick={() => redirect(`/program-effectiveness/${record.id}`)}
      sx={{ mt: 1 }}
    >
      View Effectiveness
    </Button>
  );
};
ViewEffectivenessButton.displayName = 'ViewEffectivenessButton';

export const ProgramShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="name" label="Name" />
      <DomainTagsField />
      <TextField source="orgPath" label="Org Path" />
      <FunctionField
        label="Linked Assessment IDs"
        render={(record: ProgramRecord) =>
          record?.linkedAssessmentIds?.join(', ') || 'None'
        }
      />
      <FunctionField
        label="Metric Goals"
        render={(record: ProgramRecord) => {
          const goals = record?.metricGoals ?? [];
          if (goals.length === 0) return <Typography variant="body2" color="text.secondary">None</Typography>;
          return (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Direction</TableCell>
                  <TableCell>Cadence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {goals.map((g, i) => (
                  <TableRow key={i}>
                    <TableCell>{g.metric}</TableCell>
                    <TableCell>{g.target}</TableCell>
                    <TableCell>{g.direction === 'higher_is_better' ? 'Higher is better' : 'Lower is better'}</TableCell>
                    <TableCell>{g.reviewCadence}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          );
        }}
      />
      <FunctionField
        label="Status"
        render={(record: ProgramRecord) =>
          record?.archived_at ? (
            <Chip label="Archived" size="small" color="default" />
          ) : (
            <Chip label="Active" size="small" color="success" />
          )
        }
      />
      <ViewEffectivenessButton />
    </SimpleShowLayout>
  </Show>
);
ProgramShow.displayName = 'ProgramShow';

// ---------------------------------------------------------------------------
// ProgramCreate
// ---------------------------------------------------------------------------

export const ProgramCreate = () => (
  <Create>
    <SimpleForm>
      <TextInput fullWidth source="name" label="Name" />
      <DomainTagsInput />
      <TextInput
        fullWidth
        source="linkedAssessmentIds"
        label="Linked Assessment IDs (comma-separated)"
        format={(v: string[] | undefined) => (Array.isArray(v) ? v.join(', ') : v ?? '')}
        parse={(v: string) => v.split(',').map((s) => s.trim()).filter(Boolean)}
      />
      <Box mb={1}>
        <ScopePickerInput source="orgPath" label="Org Path" />
      </Box>
      <MetricGoalsInput />
    </SimpleForm>
  </Create>
);
ProgramCreate.displayName = 'ProgramCreate';

// ---------------------------------------------------------------------------
// ProgramEdit
// ---------------------------------------------------------------------------

export const ProgramEdit = () => (
  <Edit mutationMode="pessimistic">
    <SimpleForm toolbar={<ArchivalToolbar />}>
      <TextInput fullWidth source="name" label="Name" />
      <DomainTagsInput />
      <TextInput
        fullWidth
        source="linkedAssessmentIds"
        label="Linked Assessment IDs (comma-separated)"
        format={(v: string[] | undefined) => (Array.isArray(v) ? v.join(', ') : v ?? '')}
        parse={(v: string) => v.split(',').map((s) => s.trim()).filter(Boolean)}
      />
      <Box mb={1}>
        <ScopePickerInput source="orgPath" label="Org Path" />
      </Box>
      <MetricGoalsInput />
    </SimpleForm>
  </Edit>
);
ProgramEdit.displayName = 'ProgramEdit';

// ---------------------------------------------------------------------------
// ProgramEffectiveness — standalone page (not a resource)
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, 'success' | 'error' | 'default'> = {
  on_track: 'success',
  off_track: 'error',
  insufficient_data: 'default',
};

const STATUS_LABELS: Record<string, string> = {
  on_track: 'On Track',
  off_track: 'Off Track',
  insufficient_data: 'Insufficient Data',
};

const LINE_COLORS: Record<string, string> = {
  on_track: '#2e7d32',
  off_track: '#c62828',
  insufficient_data: '#9e9e9e',
};

// Demo fixture — used only by explicit build-time VITE_DEMO_MODE=true builds.
const DEMO_EFFECTIVENESS: EffectivenessResponse = {
  programId: 'demo',
  programName: 'POTFF Pilot Program',
  domainTags: ['physical', 'psychological', 'cognitive'],
  orgPath: '/aetc/2af',
  asOf: '2026-06',
  offTrackCount: 1,
  goals: [
    {
      metric: 'pass_rate',
      target: 0.85,
      direction: 'higher_is_better',
      reviewCadence: 'monthly',
      status: 'off_track',
      current: { count: 120, sum: 96, avg: 0.80 },
      trend: [
        { period: '2026-01', count: 80,  sum: 56,  avg: 0.70 },
        { period: '2026-02', count: 95,  sum: 68,  avg: 0.72 },
        { period: '2026-03', count: 110, sum: 82,  avg: 0.75 },
        { period: '2026-04', count: 115, sum: 90,  avg: 0.78 },
        { period: '2026-05', count: 118, sum: 94,  avg: 0.80 },
        { period: '2026-06', count: 120, sum: 96,  avg: 0.80 },
      ],
    },
    {
      metric: 'dq_count',
      target: 5,
      direction: 'lower_is_better',
      reviewCadence: 'monthly',
      status: 'on_track',
      current: { count: 120, sum: 4, avg: 0.033 },
      trend: [
        { period: '2026-01', count: 80,  sum: 8,  avg: 0.10 },
        { period: '2026-02', count: 95,  sum: 7,  avg: 0.07 },
        { period: '2026-03', count: 110, sum: 6,  avg: 0.055 },
        { period: '2026-04', count: 115, sum: 5,  avg: 0.043 },
        { period: '2026-05', count: 118, sum: 4,  avg: 0.034 },
        { period: '2026-06', count: 120, sum: 4,  avg: 0.033 },
      ],
    },
  ],
};

const isDemoMode = (): boolean => import.meta.env.VITE_DEMO_MODE === 'true';

interface EffectivenessRequestState {
  key: string;
  data: EffectivenessResponse | null;
  loading: boolean;
  error: string | null;
}

export const ProgramEffectiveness = ({ programId }: { programId: string }) => {
  const requestKey = programId;
  const [request, setRequest] = useState<EffectivenessRequestState | null>(null);
  const currentRequest = request?.key === requestKey ? request : null;
  const data = currentRequest?.data ?? null;
  const loading = currentRequest?.loading ?? Boolean(programId);
  const error = currentRequest?.error ?? null;

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      if (!programId) return;
      setRequest({ key: requestKey, data: null, loading: true, error: null });
      try {
        if (isDemoMode()) {
          if (!controller.signal.aborted) {
            setRequest({ key: requestKey, data: DEMO_EFFECTIVENESS, loading: false, error: null });
          }
          return;
        }
        const json = await apiFetch(
          `programs/${encodeURIComponent(programId)}/effectiveness`,
          { signal: controller.signal },
        ) as EffectivenessResponse;
        if (controller.signal.aborted) return;
        if (json.programId !== programId) {
          throw new Error('Effectiveness response does not match the requested program.');
        }
        setRequest({ key: requestKey, data: json, loading: false, error: null });
      } catch (e: unknown) {
        if (controller.signal.aborted) return;
        const msg = e instanceof Error ? e.message : String(e);
        setRequest({ key: requestKey, data: null, loading: false, error: msg });
      }
    };
    void load();
    return () => controller.abort();
  }, [programId, requestKey]);

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Program Effectiveness
      </Typography>

      {loading && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <>
          {/* Header: name, org path, as-of */}
          <Stack direction="row" alignItems="baseline" gap={2} mb={1} flexWrap="wrap">
            <Typography variant="h6">{data.programName}</Typography>
            {data.orgPath && (
              <Typography variant="body2" color="text.secondary">{data.orgPath}</Typography>
            )}
            <Typography variant="body2" color="text.secondary">As of: {data.asOf}</Typography>
          </Stack>

          {/* Domain tag chips */}
          {data.domainTags && data.domainTags.length > 0 && (
            <Box mb={2}>
              <DomainTagChips tags={data.domainTags as DomainTag[]} />
            </Box>
          )}

          {/* Off-track banner */}
          {data.offTrackCount > 0 && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {data.offTrackCount} metric{data.offTrackCount > 1 ? 's are' : ' is'} currently <strong>off track</strong> — review required.
            </Alert>
          )}

          {/* Per-goal trend charts */}
          {data.goals.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No metric goals configured for this program.
            </Typography>
          ) : (
            <Stack gap={4}>
              {data.goals.map((goal, idx) => {
                const lineColor = LINE_COLORS[goal.status] ?? LINE_COLORS.insufficient_data;
                const currentAvg = goal.current.avg;
                return (
                  <Paper key={idx} variant="outlined" sx={{ p: 3 }}>
                    {/* Goal header */}
                    <Stack direction="row" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
                      <Typography variant="subtitle1" fontWeight={600}>
                        {goal.metric}
                      </Typography>
                      <Chip
                        label={STATUS_LABELS[goal.status] ?? goal.status}
                        size="small"
                        color={STATUS_COLORS[goal.status] ?? 'default'}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Target: <strong>{goal.target}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Current: <strong>{currentAvg !== null && currentAvg !== undefined ? currentAvg.toFixed(3) : '—'}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {goal.direction === 'higher_is_better' ? '↑ Higher is better' : '↓ Lower is better'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Cadence: {goal.reviewCadence}
                      </Typography>
                    </Stack>

                    {/* Trend chart */}
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={goal.trend} margin={{ top: 8, right: 32, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <ChartTooltip />
                        <Legend />
                        <ReferenceLine
                          y={goal.target}
                          stroke="#616161"
                          strokeDasharray="6 3"
                          label={{ value: `Target: ${goal.target}`, position: 'insideTopRight', fontSize: 11, fill: '#616161' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="avg"
                          name="avg"
                          stroke={lineColor}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};
ProgramEffectiveness.displayName = 'ProgramEffectiveness';
